/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        1) Do not import /ezlibrary/exceptions/EzExceptions.js into this module
        2) Do not import any other "exception classes" into this module
        3) Do not import /ezlibrary/ezclocker-classes/EzClass.js into this module
        4) Do not import /ezlibrary/enums/EzEnumerations.js into this module
        5) Do not import /ezlibrary/helpers/EzHelpers.js into this module
        6) Do not import the following modules into this module:
            import { EzObject } from '/ezlibrary/helpers/EzObject.js';
            import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
            import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
            import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
            import { EzString } from '/ezlibrary/helpers/EzString.js';
            import { EzArray } from '/ezlibrary/helpers/EzArray.js';
            import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
            import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
            import { EzJson } from '/ezlibrary/helpers/EzJson.js';
            import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzException } from '/ezlibrary/exceptions/EzException.js';

/**
 * @class
 * @extends {EzException}
 * @description
 * Exception for function parameters that fail validation.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * WARNING:
 *     1) Do not import /ezlibrary/exceptions/EzExceptions.js into this class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import when you also import other exception classes:
 *      import {
 *          // ... other imported exception classes ...
 *          EzBadParamException
 *     } from '/ezlibrary/exceptions/EzExceptions.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzBadParamException extends EzException {
    /**
     * @static
     * @public @readonly @property
     * Returns the default message for EzBadParamException
     * @returns {string}
     */
    static get DEFAULT_BAD_PARAM_ERROR_MESSAGE() {
        return 'A valid parameter is required';
    }

    /**
     * @private @field
     * Stores the parameter name (if provided)
     * Default is blank string.
     * @type {string}
     */
    #ezParamName = '';

    /**
     * @private @field
     * Stores the class enclosing the exception's name
     * Default: ''
     * @type {string}
     */
    #enclosingClazz = '';

    /**
     * @private @field
     * Stores information on the exceptions enclosing method
     * Default: ''
     * @type {string}
     */
    #ezEnclosingMethod = '';

    /**
     * @private @field
     * Stores the additional message (if provided)
     * Default: ''
     * @type {string}
     */
    #ezAdditionalMessage = '';

    /**
     * @public @constructor
     * Creates a new instance of EzBadParamException
     * @param {undefined|null|string} paramName
     * The parameter name is determined with the following evaluations on the provided paramName:
     *     1) If paramName is undefined or null then a blank string is assigned.
     *     2) If the paramName is a non-empty string then the paramName is assigned.
     *     3) For all other cases a blank string is assigned.
     * @param {undefined|null|string|class|function|object} enclosingClazz
     * The class name is determined with the following evaluations:
     *     1) If enclosingClazz is a string then the enclosingClazz value is assigned.
     *     2) If enclosingClazz is a class or function reference then enclosingClazz.name is assigned.
     *     3) If enclosingClazz is an object then enclosingClazz.toString() is assigned.
     *     4) Otherwise, a blank string is assigned.
     * @param {undefined|null|string|function} enclosingMethod
     * The enclosingMethod is determined with the following evaluations on the provided enclosingMethod:
     *     1) If the enclosingMethod is a non-empty string then the enclosingMethod is assigned
     *     2) If the enclosingMethod is an object then the object.toString() is assigned.
     *     3) Otherwise, a blank string is assigned.
     * @param {undefined|null|string} additionalMessage
     * The additional message is determined with the following evaluations on the provided additionalMessage:
     *     1) If additionalMessage is undefined or null then a blank string is assigned.
     *     2) If additionalMessage is a non-empty string then the string ` ${additionalMessage}` is assigned.
     *     3) For all other cases a blank string is assigned.
     * @param {undefined|null|Object} cause
     *     Default is null.
     */
    constructor(
        paramName = '',
        enclosingClazz = '',
        enclosingMethod = '',
        additionalMessage = '',
        cause = null) {
        super(
            EzBadParamException.DEFAULT_BAD_PARAM_ERROR_MESSAGE,
            {
                paramName,
                enclosingClazz,
                enclosingMethod,
                additionalMessage,
                cause,
                errorCode: 400,
            });

        // The paramName can be a string or an object wrapper with the param's name as it's only property.
        this.ezParamName = 'string' === typeof paramName
            ? paramName
            : (paramName && 'object' === typeof paramName && 0 < Object.keys(paramName).length
                ? paramName[
                Object.keys(paramName)[0]]
                : 'unnamed function parameter');

        this.enclosingClazz = enclosingClazz;

        this.ezEnclosingMethod = enclosingMethod;

        this.ezAdditionalMessage = additionalMessage;

        this.message = this.ezBuildExceptionMessage();
    }

    /**
     * @public @property @getter
     * Gets the parameter name (if provided)
     * @returns {string}
     */
    get ezParamName() {
        return this.#ezParamName;
    }
    /**
     * @public @property @setter
     * Sets the parameter name based upon the following evaluations on the provided paramName:
     *     1) If paramName is undefined or null then a blank string is assigned.
     *     2) If the paramName is a non-empty string then the paramName is assigned.
     *     3) For all other cases a blank string is assigned.
     * @param {string} ezParamName
     */
    set ezParamName(ezParamName) {
        if (!ezParamName) {
            this.#ezParamName = '';
        } else {
            this.#ezParamName = 'string' === typeof ezParamName
                ? ezParamName
                : ezParamName?.toString() || '';
        }
    }

    /**
     * @public @property @getter
     * Gets the class enclosing the exception's name
     * @returns {string}
     */
    get enclosingClazz() {
        return this.#enclosingClazz;
    }
    /**
     * @public @property @setter
     * Sets the class enclosing the exception's name.
     * Accepts a string, class reference, function reference, or object.
     * @param {string|class|function|object} enclosingClazz
     */
    set enclosingClazz(enclosingClazz) {
        if (!enclosingClazz) {
            this.#enclosingClazz = '';
        } else if ('string' === typeof enclosingClazz) {
            this.#enclosingClazz = enclosingClazz;
        } else {
            this.#enclosingClazz = 'function' === typeof enclosingClazz
                ? enclosingClazz?.name || enclosingClazz?.toString() || ''
                : enclosingClazz?.constructor?.name || enclosingClazz?.toString() || '';
        }
    }

    /**
     * @public @property @getter
     * Gets information on the exceptions enclosing method
     * @returns {string}
     */
    get ezEnclosingMethod() {
        return this.#ezEnclosingMethod;
    }
    /**
     * @public @property @setter
     * Sets information on the exceptions enclosing method
     * @param {string} ezEnclosingMethod
     */
    set ezEnclosingMethod(ezEnclosingMethod) {
        if (!ezEnclosingMethod) {
            this.#ezEnclosingMethod = '';
        } else {
            this.#ezEnclosingMethod = 'string' === typeof ezEnclosingMethod
                ? ezEnclosingMethod
                : ezEnclosingMethod?.toString() || '';
        }
    }

    /**
     * @public @property @getter
     * Gets the additional message (if provided)
     * @returns {string}
     */
    get ezAdditionalMessage() {
        return this.#ezAdditionalMessage;
    }
    /**
     * @public @property @setter
     * Sets the additional message based upon the following evaluations of the provided additionalMessage:
     *     1) If additionalMessage is undefined or null then a blank string is assigned.
     *     2) If additionalMessage is a non-empty string then the additionalMessage is assigned.
     *     3) For all other cases a blank string is assigned.
     * @param {string} additionalMessage
     */
    set ezAdditionalMessage(additionalMessage) {
        if (!additionalMessage) {
            this.#ezAdditionalMessage = '';
        } else {
            this.#ezAdditionalMessage = 'string' === typeof additionalMessage && 0 < additionalMessage.length
                ? additionalMessage
                : '';
        }
    }

    /**
     * @public @method
     * Builds the exception message from the available exception data.
     * When none of the available exception data can be used to build an error message, then the
     * default the value from EzBadParamException.DEFAULT_BAD_PARAM_ERROR_MESSAGE is returned.
     */
    ezBuildExceptionMessage() {
        let em = this.paramName && 'string' === typeof this.paramName && 0 < this.paramName?.length
            ? `A valid, non-null value is expected for parameter "${this.paramName}".`
            : 'A valid, non-null value is expected for a function parameter.';

        if (this.enclosingClazz && 'string' === typeof this.enclosingClazz && 0 < this.enclosingClazz.length) {
            em = this.ezEnclosingMethod && 'string' === typeof this.ezEnclosingMethod && 0 < this.ezEnclosingMethod.length
                ? `[Class and Function: ${this.enclosingClazz}.${this.ezEnclosingMethod}]`
                : `[Class: ${this.enclosingClazz}]`
        } else if (this.ezEnclosingMethod && 'string' === typeof this.ezEnclosingMethod && 0 <this.ezEnclosingMethod.length) {
            em = `[Function: ${this.ezEnclosingMethod}]`;
        }

        if (this.ezAdditionalMessage && 'string' === typeof this.ezAdditionalMessage && 0 < this.ezAdditionalMessage.length) {
            em = `${em} [Additional info: ${this.ezAdditionalMessage}]`;
        }


    }

    /**
     * @public @method
     * Returns the error message for this exception
     */
    toString() {
        return `${this.constructor.name}: ${this.message}`;
    }
}
